import React, { useCallback, useState } from 'react';

import _ from 'lodash';
import moment from 'moment';
import { MdRemoveRedEye } from 'react-icons/md';
import { Checkbox, Dialog, DialogContent, IconButton } from '@material-ui/core';

import API from 'utils/api';
import { useSources, useValue } from 'hooks';
import { call } from 'utils';
import { textSort } from 'components/Table';
import { DialogActions, DialogTitle, Table, Tooltip } from 'components';
import { getTemplateFriendlyType, getTemplateTypes } from 'templates';
import Preview from '../Preview/Preview';

import styles from './TemplateSelectorDialog.module.scss';

const TemplateSelectorDialog = props => {
  const [preview, setPreview] = useState();
  const [selected, setSelected] = useState();

  const filter = useValue(props.filter);
  const sources = useSources();

  const fetchData = useCallback(
    params => {
      const query =
        filter || params.filter
          ? {
              ...(filter || {}),
              ..._.mapValues(params.filter || {}, (value, key) =>
                key === 'type' ? value : { $regex: value, $options: 'i' }
              ),
            }
          : null;

      setSelected(null);
      sources.cancelAll();

      return API.getTemplates(
        {
          ..._.omit(params, 'filter'),
          query: query ? JSON.stringify(query) : undefined,
          select: 'name type params.category updatedAt',
          icon: true,
        },
        sources
      );
    },
    [filter, sources]
  );

  const renderName = it => (
    <div className={styles.name}>
      <div>
        <Checkbox
          checked={it === selected}
          onChange={() => setSelected(it)}
          color="primary"
        />
      </div>

      <div
        className={styles.icon}
        style={{ backgroundImage: it.iconUrl ? `url(${it.iconUrl})` : null }}
      />

      <div className={styles.text}>{(it.params || {}).category}</div>
    </div>
  );

  const renderActions = ({ _id: id, type }) => (
    <IconButton color="primary" onClick={() => setPreview({ id, type })}>
      <Tooltip title="Preview this template" placement="top">
        <MdRemoveRedEye />
      </Tooltip>
    </IconButton>
  );

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth={'md'}>
      <DialogTitle onClose={props.onClose}>Template List</DialogTitle>

      <DialogContent className={styles.content}>
        <Table
          data={fetchData}
          filtering
          pageSizeOptions={[5]}
          pageSize={5}
          search={false}
          selected={(selected || {})._id}
          columns={[
            {
              title: 'Name',
              field: 'params.category',
              defaultSort: 'desc',
              weight: 2,
              render: renderName,
              cellStyle: { paddingLeft: 0 },
            },
            { title: 'Display Title', field: 'name', weight: 2 },
            {
              title: 'Type',
              field: 'type',
              weight: 1.2,
              customSort: textSort('type', getTemplateFriendlyType),
              render: ({ type }) => getTemplateFriendlyType(type),
              filtering: { type: 'select', options: getTemplateTypes() },
            },
            {
              title: 'Last Modified',
              field: 'updatedAt',
              align: 'center',
              initialSort: 'desc',
              filtering: false,
              render: ({ updatedAt }) => (
                <div>{moment(updatedAt).format('YYYY/MM/DD hh:mm A')}</div>
              ),
            },
            {
              weight: 0.5,
              sorting: false,
              align: 'center',
              render: renderActions,
              filtering: false,
            },
          ]}
        />
      </DialogContent>

      <DialogActions
        actions={[
          { label: 'cancel', onClick: props.onClose },
          {
            label: 'select',
            onClick: () => call(props.onChange, selected),
            type: 'submit',
            disabled: !selected,
          },
        ]}
      />

      <Preview
        open={!!preview}
        onClose={() => setPreview(null)}
        type={preview && preview.type}
        id={preview && preview.id}
      />
    </Dialog>
  );
};

export default TemplateSelectorDialog;
